import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ShoppingBag } from 'lucide-react';

const Cart = () => {
  const [cart, setCart] = useState([]);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const savedCart = JSON.parse(localStorage.getItem('avex_cart') || '[]');
    setCart(savedCart);
  };

  const updateQuantity = (productId, newQuantity) => {
    const updatedCart = cart.map(item => 
      item.id === productId ? { ...item, quantity: Math.max(1, newQuantity) } : item
    );
    setCart(updatedCart);
    localStorage.setItem('avex_cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (productId) => {
    const updatedCart = cart.filter(item => item.id !== productId);
    setCart(updatedCart);
    localStorage.setItem('avex_cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const generateWhatsAppMessage = () => {
    let message = 'Hello Avex Enterprises,\n\nI would like to place an order with the following details:\n\n';
    
    cart.forEach((item, index) => {
      message += `${index + 1}. Product: ${item.name}\n   Quantity: ${item.quantity}\n   Price: NPR ${item.price.toLocaleString()}\n\n`;
    });
    
    message += `Total Amount: NPR ${calculateTotal().toLocaleString()}\n\n`;
    message += 'Please contact me for confirmation.\n\nThank you.';
    
    return encodeURIComponent(message);
  };

  const handleWhatsAppCheckout = () => {
    const whatsappUrl = `https://wa.me/9779816315999?text=${generateWhatsAppMessage()}`;
    window.open(whatsappUrl, '_blank');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="text-center py-16 px-4">
          <ShoppingBag className="h-24 w-24 text-[#E5E5E5] mx-auto mb-6" />
          <h2 className="font-heading text-2xl font-bold mb-4" data-testid="empty-cart-message">Your cart is empty</h2>
          <p className="text-[#888888] mb-8">Add some beautiful lighting to your cart!</p>
          <Link
            to="/products"
            className="bg-[#BFA15F] text-white hover:bg-[#A68B4F] rounded-none px-8 py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300 inline-block"
            data-testid="shop-now-btn"
          >
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-8" data-testid="cart-page-title">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white p-6 border border-[#E5E5E5] flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <Link to={`/products/${item.id}`} className="flex-shrink-0">
                  <div className="w-24 h-24 bg-[#F0F0ED] overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </div>
                </Link>

                <div className="flex-1">
                  <Link to={`/products/${item.id}`}>
                    <h3 className="font-heading text-lg font-semibold mb-1 hover:text-[#BFA15F] transition-colors">
                      {item.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-[#888888] mb-3">{item.category_name}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 border border-[#E5E5E5] hover:border-[#BFA15F] transition-colors text-sm"
                        data-testid={`decrease-qty-${item.id}`}
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-medium" data-testid={`qty-${item.id}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 border border-[#E5E5E5] hover:border-[#BFA15F] transition-colors text-sm"
                        data-testid={`increase-qty-${item.id}`}
                      >
                        +
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-[#BFA15F]" data-testid={`item-total-${item.id}`}>
                        NPR {(item.price * item.quantity).toLocaleString()}
                      </div>
                      <div className="text-sm text-[#888888]">
                        NPR {item.price.toLocaleString()} each
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => removeItem(item.id)}
                  className="text-[#888888] hover:text-red-600 transition-colors"
                  data-testid={`remove-item-${item.id}`}
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 border border-[#E5E5E5] sticky top-24">
              <h2 className="font-heading text-xl font-semibold mb-6">Order Summary</h2>
              
              <div className="space-y-3 mb-6 pb-6 border-b border-[#E5E5E5]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#888888]">Subtotal</span>
                  <span className="font-medium" data-testid="cart-subtotal">NPR {calculateTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#888888]">Items</span>
                  <span className="font-medium">{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
                </div>
              </div>

              <div className="flex justify-between mb-6 text-lg font-bold">
                <span>Total</span>
                <span className="text-[#BFA15F]" data-testid="cart-total">NPR {calculateTotal().toLocaleString()}</span>
              </div>

              <button
                onClick={handleWhatsAppCheckout}
                className="w-full bg-[#25D366] text-white hover:bg-[#20BA5A] py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 mb-3"
                data-testid="whatsapp-checkout-btn"
              >
                Order via WhatsApp
              </button>

              <Link
                to="/products"
                className="block w-full border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white py-4 text-center uppercase tracking-widest text-sm font-medium transition-all duration-300"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </Link>

              <div className="mt-6 p-4 bg-[#F9F9F7] text-sm text-[#4A4A4A]">
                <p className="font-medium mb-2">Why WhatsApp?</p>
                <ul className="space-y-1 text-xs">
                  <li>• Direct communication with our team</li>
                  <li>• Instant order confirmation</li>
                  <li>• Personalized assistance</li>
                  <li>• Flexible payment options</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;